import Intercom from '@intercom/messenger-js-sdk'
import {PrismicPreview} from '@prismicio/next/pages'
import {HubspotProvider} from 'next-hubspot'
import type {AppProps} from 'next/app'
import dynamic from 'next/dynamic'
import localFont from 'next/font/local'
import {useRouter} from 'next/router'
import prismicConfig from 'prismic.config.json'
import {useEffect} from 'react'
import 'src/assets/global.css'
import {AppErrorBoundary} from 'src/common/AppErrorBoundary'
import {CookiesProvider} from 'src/common/CookiesContext'
import {FeedbackTool} from 'src/common/FeedbackTool'
import {Meta} from 'src/common/Meta'

const OnRouteChangeTracker = dynamic(
  () => import('src/common/OnRouteChangeTracker').then((mod) => mod.OnRouteChangeTracker),
  {ssr: false},
)

const BrowserLogging = dynamic(() => import('src/common/logger/BrowserLogging').then((mod) => mod.BrowserLogging), {
  ssr: false,
})

const repositoryName = process.env.NEXT_PUBLIC_PRISMIC_REPOSITORY_NAME || prismicConfig.repositoryName

const mainFont = localFont({
  display: 'swap',

  src: [
    {path: '../public/fonts/Systemia-Light.woff', weight: '300'},
    {path: '../public/fonts/Systemia-Regular.woff', weight: '400'},
    {path: '../public/fonts/Systemia-Medium.woff', weight: '500'},
    {path: '../public/fonts/Systemia-Semibold.woff', weight: '600'},
    {path: '../public/fonts/Systemia-Bold.woff', weight: '700'},
  ],

  variable: '--font-main',
})

export default function App({Component, pageProps}: AppProps) {
  const router = useRouter()

  // Initialize Intercom and hide it on specific pages
  useEffect(() => {
    const HIDE_INTERCOM_ROUTES = ['/stresstimonials', '/chasing-zero-game']

    if (process.env.NODE_ENV !== 'development') {
      const currentPath = router.asPath.split('?')[0]
      const shouldHide = HIDE_INTERCOM_ROUTES.includes(currentPath)

      // Hide or show Intercom based on current page
      if (window.Intercom) {
        window.Intercom('update', {
          hide_default_launcher: shouldHide,
        })
      } else {
        Intercom({
          app_id: 'kvadbg23',
          hide_default_launcher: shouldHide,
          action_color: '#0C3A31',
          background_color: '#0C3A31',
        })
      }
    }
  }, [router.asPath])

  return (
    <PrismicPreview repositoryName={repositoryName}>
      <style jsx global>
        {`
          html {
            --font-main: ${mainFont.style.fontFamily};
          }
        `}
      </style>
      <Meta>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta charSet="utf-8" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/favicon/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/favicon/apple-touch-icon.png?v=2" />
        <link rel="manifest" href="/favicon/manifest.webmanifest" />
      </Meta>
      <HubspotProvider>
        <CookiesProvider>
          <AppErrorBoundary>
            <Component {...pageProps} />
          </AppErrorBoundary>
        </CookiesProvider>
      </HubspotProvider>
      <OnRouteChangeTracker />
      <BrowserLogging />
      <FeedbackTool />
    </PrismicPreview>
  )
}
